import { toast } from '@/components/toasts/toast.helpers'

import { fetchingAPI, apiService } from '../../api'
import { UNCATEGORIZED_TAGS } from '../../utils/constants'
import {
  setTags,
  setCategories,
  addCategory,
  updateOrderOfTags,
} from './organizationTagManager.redux'
import { setUsers } from './organizationUsers.redux'

export const fetchTagBoard =
  ({ organizationId }) =>
  async (dispatch) => {
    try {
      const [tagCategories, tags] = await Promise.all([
        fetchingAPI(
          `${apiService.core}/api/organizations/${organizationId}/tags/categories`,
          'GET',
          dispatch
        ),
        fetchingAPI(`${apiService.core}/api/organizations/${organizationId}/tags`, 'GET', dispatch),
      ])
      tags.sort((a, b) => a.name.localeCompare(b.name))

      // Both dispatches land in the same tick so the board repaints once,
      // instead of a categories-only frame followed by the tags.
      dispatch(setCategories(tagCategories))
      dispatch(setTags(tags))
    } catch (err) {
      toast.error('Failed to fetch tags', { cause: err })
    }
  }

export const createTags =
  ({ tags, category, organizationId, toggleLoading, reloadTags }) =>
  async (dispatch) => {
    try {
      const body = JSON.stringify({
        tagNames: tags.tagNames,
        tag_category_id: category === '' ? null : category,
      })

      await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags`,
        'POST',
        dispatch,
        body
      )

      reloadTags({ organizationId, toggleLoading })
      toast.success('Tag created')
    } catch (err) {
      toast.error('Adding tag failed. Tags cannot exceed 40 characters or be duplicates.', {
        cause: err,
      })
    } finally {
      if (toggleLoading) toggleLoading()
    }
  }

export const createCategory =
  ({ tagCategories, organizationId, toggleLoading, reloadTags, allCategories }) =>
  async (dispatch) => {
    try {
      const body = JSON.stringify({
        name: tagCategories,
        organization_id: organizationId,
        sort_id: allCategories.columnOrder.length - 1,
      })
      const category = await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags/categories`,
        'POST',
        dispatch,
        body
      )

      dispatch(addCategory(category))
      await reloadTags({ organizationId })
      toast.success('Category created')
    } catch (err) {
      toast.error(`${err?.error || 'Failed to create category'}`, { cause: err })
    } finally {
      if (toggleLoading) toggleLoading()
    }
  }

export const updateTagCategories = (tagId, destinationOrg) => async (dispatch) => {
  const orgId =
    destinationOrg.title.toLowerCase().split(' ').join('') ===
    UNCATEGORIZED_TAGS.toLowerCase().split(' ').join('')
      ? null
      : destinationOrg.categoryId

  const body = JSON.stringify({ tag_category_id: orgId })
  try {
    await fetchingAPI(`${apiService.core}/api/tags/${tagId}`, 'PATCH', dispatch, body)
  } catch {
    toast.error('Failed to update tag category')
  }
}

export const deleteCategory =
  ({ category, reloadTags, toggleLoading, organizationId }) =>
  async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags/categories/${category.categoryId}`,
        'DELETE',
        dispatch
      )
      if (reloadTags) reloadTags({ organizationId, toggleLoading })
      toast.success('Category deleted')
    } catch {
      toast.error('Failed to delete category')
    } finally {
      if (toggleLoading) toggleLoading()
    }
  }

export const updateCategory =
  ({ category, updates, reloadTags, toggleLoading, organizationId }) =>
  (dispatch) => {
    const body = JSON.stringify(updates)
    return fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}/tags/categories/${category.categoryId}`,
      'PATCH',
      dispatch,
      body
    )
      .then((cat) => {
        if (reloadTags) reloadTags({ organizationId, toggleLoading })
        return cat
      })
      .catch(() => {
        if (toggleLoading) {
          toggleLoading()
        }
        // Resync the board with the server: the optimistic reorder has already rendered.
        if (reloadTags) {
          reloadTags({ organizationId, toggleLoading })
        }
        toast.error('Failed to update category')
      })
  }

export const deleteTag =
  ({ tag, reloadTags, toggleLoading, organizationId }) =>
  async (dispatch) => {
    try {
      await fetchingAPI(`${apiService.core}/api/tags/${tag.tagId}`, 'DELETE', dispatch)
      reloadTags({ organizationId, toggleLoading })
      toast.success('Tag deleted')
    } catch {
      toast.error('Failed to delete tag')
    } finally {
      if (toggleLoading) toggleLoading()
    }
  }

export const deleteAllTags =
  ({ organizationId, toggleLoading, reloadTags }) =>
  async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags`,
        'DELETE',
        dispatch
      )
      reloadTags({ organizationId, toggleLoading })
    } catch {
      toast.error('Failed to delete all tags')
    } finally {
      if (toggleLoading) toggleLoading()
    }
  }

export const updateTagsInState = (tagStore) => (dispatch) => {
  return dispatch(updateOrderOfTags(tagStore))
}

export const updateUsers = (users) => (dispatch) => {
  return dispatch(setUsers(users))
}
